import { CANONICAL_FINANCIAL_REPORTS } from './report-taxonomy.js';
import { NEPAL_JURISDICTION_RULES, NEPAL_SOURCE_AUTHORITIES, } from './nepal-jurisdiction-registry.js';
export const NEPAL_CALENDAR = Object.freeze({
    id: 'NEPAL_FISCAL_CALENDAR',
    canonicalCalculationCalendar: 'GREGORIAN',
    displayCalendars: ['GREGORIAN', 'BIKRAM_SAMBAT'],
    fiscalYearStart: 'SHRAWAN',
    fiscalYearEnd: 'ASHADH',
    canonicalTimeZone: 'Asia/Kathmandu',
    dualDateDisplay: true,
    dualDateExport: true,
});
export const NEPAL_PRIVACY_POLICIES = Object.freeze([
    {
        identifier: 'BOID',
        classification: 'PERSONAL',
        displayPolicy: 'MASK',
        auditAccess: true,
        exportRequiresElevatedPermission: true,
    },
    {
        identifier: 'PAN',
        classification: 'PERSONAL',
        displayPolicy: 'MASK',
        auditAccess: true,
        exportRequiresElevatedPermission: true,
    },
    {
        identifier: 'BANK_ACCOUNT',
        classification: 'FINANCIAL',
        displayPolicy: 'MASK',
        auditAccess: true,
        exportRequiresElevatedPermission: true,
    },
    {
        identifier: 'BENEFICIAL_OWNER',
        classification: 'REGULATORY',
        displayPolicy: 'FULL_WITH_PERMISSION',
        auditAccess: true,
        exportRequiresElevatedPermission: true,
    },
]);
const nepalReports = CANONICAL_FINANCIAL_REPORTS.filter((report) => report.requiredJurisdiction === 'NEPAL');
const reportIdsFor = (category) => nepalReports.filter((report) => report.category === category).map((report) => report.id);
const rulesForModule = (module) => NEPAL_JURISDICTION_RULES.filter((rule) => rule.module === module).map((rule) => rule.ruleId);
export const NEPAL_DOMAIN_MODULES = Object.freeze([
    {
        id: 'calendar',
        enabledByDefault: true,
        resourceIds: ['nepal.calendar', 'nepal.fiscal-periods', 'nepal.holidays'],
        reportIds: [],
        policyIds: rulesForModule('calendar'),
        sourceAuthorityIds: ['MINISTRY_OF_FINANCE_NEPAL', 'NEPAL_LAW_COMMISSION'],
    },
    {
        id: 'locale',
        enabledByDefault: true,
        resourceIds: ['nepal.locale', 'nepal.dual-date-format'],
        reportIds: [],
        policyIds: [],
        sourceAuthorityIds: ['NEPAL_LAW_COMMISSION'],
    },
    {
        id: 'nepse',
        enabledByDefault: true,
        resourceIds: [
            'nepal.nepse.instruments',
            'nepal.nepse.prices',
            'nepal.nepse.indices',
            'nepal.nepse.market-calendar',
        ],
        reportIds: reportIdsFor('NEPAL_NEPSE'),
        policyIds: rulesForModule('nepse'),
        sourceAuthorityIds: ['NEPSE'],
    },
    {
        id: 'cdsc',
        enabledByDefault: false,
        regulatedEntityTypes: ['PORTFOLIO_MANAGER', 'BROKER', 'DEPOSITORY_PARTICIPANT'],
        resourceIds: [
            'nepal.cdsc.boid-accounts',
            'nepal.cdsc.positions',
            'nepal.cdsc.obligations',
            'nepal.cdsc.reconciliation',
        ],
        reportIds: reportIdsFor('NEPAL_CDSC'),
        policyIds: rulesForModule('cdsc'),
        sourceAuthorityIds: ['CDSC'],
    },
    {
        id: 'sebon',
        enabledByDefault: false,
        regulatedEntityTypes: ['PORTFOLIO_MANAGER', 'INVESTMENT_MANAGER', 'BROKER', 'MUTUAL_FUND'],
        resourceIds: [
            'nepal.sebon.licenses',
            'nepal.sebon.filings',
            'nepal.sebon.inspections',
            'nepal.sebon.acknowledgements',
        ],
        reportIds: reportIdsFor('NEPAL_SEBON'),
        policyIds: rulesForModule('sebon'),
        sourceAuthorityIds: ['SEBON'],
    },
    {
        id: 'tax',
        enabledByDefault: true,
        resourceIds: [
            'nepal.tax.rules',
            'nepal.tax.lots',
            'nepal.tax.certificates',
            'nepal.tax.exceptions',
        ],
        reportIds: reportIdsFor('NEPAL_TAX'),
        policyIds: rulesForModule('tax'),
        sourceAuthorityIds: ['INLAND_REVENUE_DEPARTMENT_NEPAL', 'MINISTRY_OF_FINANCE_NEPAL'],
    },
    {
        id: 'nfrs',
        enabledByDefault: false,
        regulatedEntityTypes: ['PORTFOLIO_MANAGER', 'INVESTMENT_MANAGER', 'MUTUAL_FUND'],
        resourceIds: [
            'nepal.nfrs.classification',
            'nepal.nfrs.fair-value',
            'nepal.nfrs.impairment',
            'nepal.nfrs.disclosures',
        ],
        reportIds: reportIdsFor('NEPAL_NFRS'),
        policyIds: rulesForModule('nfrs'),
        sourceAuthorityIds: ['ACCOUNTING_STANDARDS_BOARD_NEPAL', 'ICAN'],
    },
    {
        id: 'aml',
        enabledByDefault: false,
        regulatedEntityTypes: ['PORTFOLIO_MANAGER', 'INVESTMENT_MANAGER', 'BROKER', 'MUTUAL_FUND'],
        resourceIds: [
            'nepal.aml.cdd',
            'nepal.aml.edd',
            'nepal.aml.screening',
            'nepal.aml.cases',
            'nepal.aml.filings',
        ],
        reportIds: reportIdsFor('NEPAL_AML'),
        policyIds: rulesForModule('aml'),
        sourceAuthorityIds: ['FIU_NEPAL'],
    },
    {
        id: 'mutual-funds',
        enabledByDefault: false,
        regulatedEntityTypes: ['MUTUAL_FUND'],
        resourceIds: [
            'nepal.mutual-funds.schemes',
            'nepal.mutual-funds.nav',
            'nepal.mutual-funds.unit-holders',
            'nepal.mutual-funds.subscriptions',
            'nepal.mutual-funds.redemptions',
        ],
        reportIds: reportIdsFor('NEPAL_MUTUAL_FUNDS'),
        policyIds: rulesForModule('mutual-funds'),
        sourceAuthorityIds: ['SEBON'],
    },
    {
        id: 'reporting',
        enabledByDefault: true,
        resourceIds: [
            'nepal.reporting.definitions',
            'nepal.reporting.runs',
            'nepal.reporting.artifacts',
            'nepal.reporting.filings',
        ],
        reportIds: nepalReports.map((report) => report.id),
        policyIds: rulesForModule('reporting'),
        sourceAuthorityIds: ['SEBON', 'INLAND_REVENUE_DEPARTMENT_NEPAL', 'FIU_NEPAL', 'CDSC', 'NEPSE'],
    },
    {
        id: 'validation',
        enabledByDefault: true,
        resourceIds: ['nepal.validation.schemas', 'nepal.validation.results'],
        reportIds: [],
        policyIds: [],
        sourceAuthorityIds: [],
    },
    {
        id: 'source-authorities',
        enabledByDefault: true,
        resourceIds: ['nepal.source-authorities', 'nepal.rule-versions'],
        reportIds: [],
        policyIds: NEPAL_JURISDICTION_RULES.map((rule) => rule.ruleId),
        sourceAuthorityIds: NEPAL_SOURCE_AUTHORITIES.map((source) => source.id),
    },
]);
/**
 * Nepal's first generic content-pack instance. It is explicitly UNVERIFIED:
 * source discovery and catalogue metadata must not be mistaken for approved
 * legal or accounting interpretations.
 *
 * 1.2.0 — NFRS reporting depth: 20 additional NFRS 7/9/13 and presentation
 * reports, NFRS parameter conventions on existing entries, and two new nfrs
 * module jurisdiction rules (expected credit loss, NFRS 7 disclosures).
 */
export const NEPAL_JURISDICTION_PACKAGE = Object.freeze({
    schemaVersion: 'jurisdiction-content-package.v1',
    packageId: 'nepal.capital-markets',
    jurisdictionId: 'NEPAL',
    version: '1.2.0',
    platformContractRange: '>=1 <2',
    lifecycleStatus: 'DRAFT',
    contentStatus: 'UNVERIFIED',
    effectiveFrom: '2000-01-01',
    regulatedEntityTypes: [
        'PORTFOLIO_MANAGER',
        'INVESTMENT_MANAGER',
        'BROKER',
        'DEPOSITORY_PARTICIPANT',
        'MUTUAL_FUND',
    ],
    currencies: ['NPR'],
    languages: ['en', 'ne'],
    modules: NEPAL_DOMAIN_MODULES,
    sourceAuthorities: NEPAL_SOURCE_AUTHORITIES.map((authority) => ({
        id: authority.id,
        displayName: authority.name,
        authorityType: authority.authorityType,
        officialPublicationRequired: authority.officialPublicationRequired,
    })),
    sourceDocuments: [],
    interpretationReviews: [],
    contentVersions: [],
    policyBindings: [
        { familyId: 'CORPORATE_ACTION_EX_DATE', referenceKey: 'DEFAULT' },
        { familyId: 'RECONCILIATION_POLICY', referenceKey: 'NEPAL_DEFAULT_RECONCILIATION' },
        {
            familyId: 'FINANCIAL_CONTROL_RECONCILIATION',
            referenceKey: 'NEPAL_ORG_ADMIN_CUTOFF_CONTROLS',
        },
        { familyId: 'VALUATION_OVERRIDE_POLICY', referenceKey: 'NEPAL' },
        { familyId: 'FIXED_INCOME_ADJUSTMENT_STRATEGY', referenceKey: 'FOLLOWING_ADJUSTMENT' },
        { familyId: 'FIXED_INCOME_CALENDAR', referenceKey: 'NEPAL_MARKET_CALENDAR' },
        { familyId: 'REGULATORY_REPORTING_DEADLINE', referenceKey: 'SEBON_QUARTERLY_PORTFOLIO' },
        { familyId: 'FIXED_INCOME_HOLIDAY_CALENDAR', referenceKey: 'NEPAL_MARKET_HOLIDAYS' },
        { familyId: 'FIXED_INCOME_CONVENTION', referenceKey: 'NEPAL_STANDARD_DEBT' },
        { familyId: 'FIXED_INCOME_RATE_OBSERVATION', referenceKey: 'NEPAL_REFERENCE_RATE_2026_07' },
        { familyId: 'FIXED_INCOME_AMORTIZATION', referenceKey: 'NEPAL_STANDARD_DEBT' },
        { familyId: 'FIXED_INCOME_LIFECYCLE', referenceKey: 'NEPAL_STANDARD_DEBT' },
        { familyId: 'ACCOUNTING_CLASSIFICATION', referenceKey: 'NEPAL_NFRS_TEST_ONLY' },
        { familyId: 'EXPECTED_CREDIT_LOSS', referenceKey: 'NEPAL_NFRS9_TEST_ONLY' },
        { familyId: 'FAIR_VALUE_MEASUREMENT', referenceKey: 'NEPAL_NFRS13_TEST_ONLY' },
        { familyId: 'ACCOUNTING_DISCLOSURE', referenceKey: 'NEPAL_NFRS_TEST_ONLY' },
        { familyId: 'RELATED_PARTY_CONFLICT', referenceKey: 'NEPAL_CONFLICT_TEST_ONLY' },
        { familyId: 'CONTINUOUS_SCREENING', referenceKey: 'NEPAL_AML_SCREENING_TEST_ONLY' },
        { familyId: 'REGULATORY_CASE_REPORTING', referenceKey: 'NEPAL_FIU_REPORTING_TEST_ONLY' },
        {
            familyId: 'CLIENT_AGREEMENT_REQUIREMENTS',
            referenceKey: 'NEPAL_PORTFOLIO_AGREEMENT_TEST_ONLY',
        },
        { familyId: 'CLIENT_STATEMENT_POLICY', referenceKey: 'NEPAL_CLIENT_STATEMENT_TEST_ONLY' },
        { familyId: 'COMPLAINT_GOVERNANCE', referenceKey: 'NEPAL_COMPLAINT_TEST_ONLY' },
        { familyId: 'CUSTODY_IDENTIFIER_POLICY', referenceKey: 'NEPAL_CDSC_IDENTIFIER_TEST_ONLY' },
        { familyId: 'PRIVACY_RIGHTS', referenceKey: 'NEPAL_PRIVACY_ACT_TEST_ONLY' },
        {
            familyId: 'INTEGRATION_MESSAGE_POLICY',
            referenceKey: 'NEPAL_SECURE_INTEGRATION_TEST_ONLY',
        },
        { familyId: 'MONETARY_APPROVAL_POLICY', referenceKey: 'NEPAL_ORG_APPROVAL_TEST_ONLY' },
        { familyId: 'RISK_ANALYTICS_POLICY', referenceKey: 'NEPAL_PORTFOLIO_RISK_TEST_ONLY' },
    ],
});
export const NEPAL_PACKAGE_RUNTIME = Object.freeze({
    jurisdictionId: 'NEPAL',
    version: '1.2.0',
    calendar: NEPAL_CALENDAR,
    modules: NEPAL_DOMAIN_MODULES,
    reports: nepalReports,
    rules: NEPAL_JURISDICTION_RULES,
    sourceAuthorities: NEPAL_SOURCE_AUTHORITIES,
    privacyPolicies: NEPAL_PRIVACY_POLICIES,
});
export function validateNepalPackageRuntime() {
    const errors = [];
    const moduleIds = new Set();
    for (const module of NEPAL_DOMAIN_MODULES) {
        if (moduleIds.has(module.id))
            errors.push(`${module.id}: duplicate Nepal module`);
        moduleIds.add(module.id);
        if (module.resourceIds.length === 0)
            errors.push(`${module.id}: at least one resource is required`);
        for (const reportId of module.reportIds) {
            if (!nepalReports.some((report) => report.id === reportId))
                errors.push(`${module.id}: unknown report ${reportId}`);
        }
        for (const ruleId of module.policyIds) {
            if (!NEPAL_JURISDICTION_RULES.some((rule) => rule.ruleId === ruleId))
                errors.push(`${module.id}: unknown rule ${ruleId}`);
        }
        for (const sourceId of module.sourceAuthorityIds) {
            if (!NEPAL_SOURCE_AUTHORITIES.some((source) => source.id === sourceId))
                errors.push(`${module.id}: unknown source authority ${sourceId}`);
        }
    }
    if (NEPAL_CALENDAR.canonicalCalculationCalendar !== 'GREGORIAN')
        errors.push('Nepal calculations must use the canonical Gregorian calendar');
    if (!NEPAL_CALENDAR.displayCalendars.includes('BIKRAM_SAMBAT'))
        errors.push('Bikram Sambat display support is required');
    return errors;
}
